// src/pages/ConfiguracionRecibos.js
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { FaFileInvoiceDollar, FaUpload, FaArrowLeft, FaQuestionCircle, FaLock, FaCoins, FaInfoCircle, FaFolderPlus, FaFolder, FaTags, FaEdit, FaTrash, FaMagic, FaCheck, FaExchangeAlt, FaPlus, FaTimes, FaExclamationTriangle, FaCheckCircle, FaSnowflake, FaCalendarAlt, FaProjectDiagram } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { db, storage } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import ConfirmDialog from '../components/ConfirmDialog';
import { esPartidaContableResidente, esConceptoTesoreria, getCategoriaConceptoInfo } from '../utils/conceptosContables';
import { PLANTILLA_EGRESOS_AGAVE_V, normalizarCategoriasEgreso, obtenerTodosConceptosEgreso, obtenerCategoriasQueCompartenConcepto } from '../utils/bolsasFinancierasUtils';
import './Configuraciones.css';
import './ConfiguracionRecibos.css';

const defaultReceiptConfig = {
    logoUrl: '',
    logoWidth: '80',
    logoHeight: '50',
    selloUrl: '',
    selloWidth: '100',
    selloHeight: '60',
    condominioNombre: '',
    administradorNombre: '',
    condominioDireccion: '',
    adminDireccion: '',
    datosFacturacion: '',
    razonSocial: '',
    rfc: '',
    folioSerie: 'REC',
    folioConsecutivo: 1,
    folioSerieEgreso: 'EGR',
    folioConsecutivoEgreso: 1,
    fechaFormato: 'corta',
    moneda: 'MXN',
};

const defaultConceptos = [
    'Cuota de Mantenimiento',
    'Cuota Extraordinaria',
    'Adeudos anteriores',
    'Sanciones Administrativas',
    'Amenidades',
    'Servicios',
    'Recargos o intereses',
    'Descuentos / Pronto pago',
    'Pago Anual Cuota Mantto',
];

const defaultConceptosEgreso = [
    'Mantenimiento',
    'Servicios Públicos',
    'Seguridad',
    'Administrativos',
    'Limpieza',
    'Jardineria',
    'Seguros y Contingencias',
];

// Utilidades: normalizar etiquetas y asegurar que "Otros" quede al final sin duplicados
const normalizeLabel = (label = '') => label.toString().trim();
const reorderOtrosLast = (list = []) => {
    const seen = new Set();
    const cleaned = [];
    let hasOtros = false;

    list.forEach((item) => {
        const norm = normalizeLabel(item);
        if (!norm) return;
        const isOtros = norm.toLowerCase() === 'otros';
        if (isOtros) {
            hasOtros = true;
            return;
        }
        if (!seen.has(norm.toLowerCase())) {
            seen.add(norm.toLowerCase());
            cleaned.push(norm);
        }
    });

    if (hasOtros) cleaned.push('Otros');
    return cleaned;
};

// Conceptos base obligatorios de Categoría A (Contabilidad de Residentes)
const CONCEPTOS_BASE_A = [
    'Cuota de Mantenimiento',
    'Cuota Extraordinaria',
    'Adeudos anteriores',
    'Sanciones Administrativas',
    'Recargos o intereses',
    'Descuentos / Pronto pago',
    'Pago Anual Cuota Mantto',
];

// Conceptos protegidos: necesarios para cálculos y no deben eliminarse
const BLOCKED_CONCEPTS = new Set([
    'Cuota de Mantenimiento',
    'Cuota Extraordinaria',
    'Recargos o intereses',
    'Descuentos / Pronto pago',
    'Adeudos anteriores',
    'Sanciones Administrativas',
    'Sansiones Administrativas',
    'Multas Administrativas',
    'Pago Anual Cuota Mantto',
]);

// Normaliza y asegura que todos los conceptos de Categoría A estén presentes sin "MULTAS"
const asegurarConceptosCategoriaA = (list = []) => {
    const rawList = Array.isArray(list) ? list : [];
    // 1. Limpieza y migración de conceptos
    const mapped = rawList
        .filter(Boolean)
        .map((item) => {
            const str = String(item).trim();
            if (str.toLowerCase() === 'multas administrativas' || str.toLowerCase() === 'multa administrativa') {
                return 'Sanciones Administrativas';
            }
            if (str.toLowerCase() === 'cuota extraniordinaria') {
                return 'Cuota Extraordinaria';
            }
            return str;
        });

    const seen = new Set();
    const result = [];

    // Agregar conceptos migrados sin duplicados
    mapped.forEach((item) => {
        const lower = item.toLowerCase();
        if (!seen.has(lower)) {
            seen.add(lower);
            result.push(item);
        }
    });

    // Asegurar que todos los conceptos base de Categoría A estén en la lista
    CONCEPTOS_BASE_A.forEach((conceptoBase) => {
        if (!seen.has(conceptoBase.toLowerCase())) {
            seen.add(conceptoBase.toLowerCase());
            result.push(conceptoBase);
        }
    });

    return reorderOtrosLast(result);
};

const ConfiguracionRecibos = () => {
    const { fraccionamientoId } = useAuth();
    const { showNotification } = useNotification();

    const pageTopRef = useRef(null);
    const [tab, setTab] = useState(0);
    const [receiptConfig, setReceiptConfig] = useState(defaultReceiptConfig);
    const [conceptos, setConceptos] = useState(defaultConceptos);
    const [newConcept, setNewConcept] = useState('');
    const [conceptosEgreso, setConceptosEgreso] = useState(defaultConceptosEgreso);
    const [newConceptEgreso, setNewConceptEgreso] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [showUnsavedPrompt, setShowUnsavedPrompt] = useState(false);
    const [pendingTab, setPendingTab] = useState(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [confirmDeleteLabel, setConfirmDeleteLabel] = useState('');
    const [confirmDeleteEgresoOpen, setConfirmDeleteEgresoOpen] = useState(false);
    const [confirmDeleteEgresoLabel, setConfirmDeleteEgresoLabel] = useState('');

    // Estados para modal de precio fijo
    const [showPriceModal, setShowPriceModal] = useState(false);
    const [conceptPrice, setConceptPrice] = useState('');
    const [pendingConcept, setPendingConcept] = useState('');

    // Estados para Categorías y Bolsas de Egresos
    const [categoriasEgreso, setCategoriasEgreso] = useState(PLANTILLA_EGRESOS_AGAVE_V);
    const [showModalCategoria, setShowModalCategoria] = useState(false);
    const [categoriaEditando, setCategoriaEditando] = useState(null);
    const [categoriaForm, setCategoriaForm] = useState({
        nombre: '',
        descripcion: '',
        color: '#10b981',
        conceptosIngresoAsociados: ['Cuota de Mantenimiento'],
        // Bolsa de Proyecto Especial
        esBolsaProyecto: false,
        cuotaExtraAsociadaId: null,
        montoCuotaExtra: '',
        frecuenciaCuotaExtra: 'monthly',
        mesInicioCuotaExtra: new Date().getMonth() + 1,
        duracionMesesCuotaExtra: 6,
        // Congelación periódica de remanente
        congelacion: {
            habilitada: false,
            periodicidad: 'mensual',
            saldoCongeladoAcumulado: 0
        }
    });
    const [categoriaParaEliminar, setCategoriaParaEliminar] = useState(null);
    const [nuevoConceptoTextoPorCat, setNuevoConceptoTextoPorCat] = useState({});
    const [nuevoConceptoTipoPorCat, setNuevoConceptoTipoPorCat] = useState({});
    const [confirmPlantillaOpen, setConfirmPlantillaOpen] = useState(false);
    const [showUnassignedPopover, setShowUnassignedPopover] = useState(false);

    // Conceptos de cobro de recibo que aún no están asignados para fondear ninguna categoría de egreso
    const conceptosSinAsignar = useMemo(() => {
        const asignadosSet = new Set();
        (categoriasEgreso || []).forEach(cat => {
            (cat.conceptosIngresoAsociados || []).forEach(ci => {
                if (ci) asignadosSet.add(String(ci).trim().toLowerCase());
            });
        });
        return (conceptos || []).filter(c => c && !asignadosSet.has(String(c).trim().toLowerCase()));
    }, [conceptos, categoriasEgreso]);

    useEffect(() => {
        // Scroll al inicio manejado por ScrollToTop global
        if (!fraccionamientoId) return;
        let active = true;
        const load = async () => {
            try {
                setLoading(true);
                const refFracc = doc(db, 'fraccionamientos', fraccionamientoId);
                const snap = await getDoc(refFracc);
                if (!active) return;
                const stored = snap.exists() ? snap.data()?.configuracionRecibos : null;

                // Si no hay configuración guardada, auto-guardar los defaults
                const hasNoConfig = !stored || typeof stored !== 'object';
                if (hasNoConfig) {
                    try {
                        setSaving(true);
                        const conceptosObject = defaultConceptos.reduce((acc, label, index) => {
                            acc[`concepto_${index + 1}`] = label;
                            return acc;
                        }, {});
                        const defaultCats = PLANTILLA_EGRESOS_AGAVE_V;
                        const conceptosEgresoFlat = obtenerTodosConceptosEgreso(defaultCats);
                        const conceptosEgresoObject = conceptosEgresoFlat.reduce((acc, label, index) => {
                            acc[`concepto_egreso_${index + 1}`] = label;
                            return acc;
                        }, {});
                        await setDoc(refFracc, {
                            configuracionRecibos: {
                                ...defaultReceiptConfig,
                                conceptos: conceptosObject,
                                conceptosList: defaultConceptos,
                                conceptosEgreso: conceptosEgresoObject,
                                conceptosEgresoList: conceptosEgresoFlat,
                                categoriasEgreso: defaultCats,
                            },
                        }, { merge: true });
                        setLastSavedAt(new Date());
                        console.log('ConfiguracionRecibos: Configuración por defecto inicializada automáticamente');
                    } catch (saveErr) {
                        console.warn('ConfiguracionRecibos: no se pudo inicializar configuración por defecto', saveErr);
                    } finally {
                        setSaving(false);
                    }
                    // Usar los defaults locales
                    setReceiptConfig(defaultReceiptConfig);
                    setConceptos(defaultConceptos);
                    setConceptosEgreso(defaultConceptosEgreso);
                    setCategoriasEgreso(PLANTILLA_EGRESOS_AGAVE_V);
                    setDirty(false);
                } else if (stored && typeof stored === 'object') {
                    setReceiptConfig((prev) => ({
                        ...prev,
                        ...stored,
                    }));
                    const storedConcepts = Array.isArray(stored.conceptos)
                        ? stored.conceptos
                        : stored.conceptosList
                            ? stored.conceptosList
                            : stored.conceptos && typeof stored.conceptos === 'object'
                                ? Object.values(stored.conceptos)
                                : null;
                    if (storedConcepts && storedConcepts.length > 0) {
                        setConceptos(asegurarConceptosCategoriaA(storedConcepts));
                    }

                    const storedConceptsEgreso = Array.isArray(stored.conceptosEgreso)
                        ? stored.conceptosEgreso
                        : stored.conceptosEgresoList
                            ? stored.conceptosEgresoList
                            : stored.conceptosEgreso && typeof stored.conceptosEgreso === 'object'
                                ? Object.values(stored.conceptosEgreso)
                                : null;
                    if (storedConceptsEgreso && storedConceptsEgreso.length > 0) {
                        setConceptosEgreso(reorderOtrosLast(storedConceptsEgreso.filter(Boolean)));
                    }

                    // Cargar y normalizar las categorías de egreso
                    const loadedCats = normalizarCategoriasEgreso(stored.categoriasEgreso, storedConceptsEgreso);
                    setCategoriasEgreso(loadedCats);

                    setDirty(false);
                }
            } catch (error) {
                console.error('ConfiguracionRecibos: no se pudo cargar la configuración', error);
                showNotification('No se pudo cargar la configuración de recibos.', 'error');
            } finally {
                if (active) setLoading(false);
            }
        };
        load();
        return () => { active = false; };
    }, [fraccionamientoId]);

    // Scroll to top when tab changes (solo cuando realmente cambia, no en montaje inicial)
    const prevTabRef = useRef(tab);
    useEffect(() => {
        // Solo hacer scroll si realmente cambió el tab (no en el primer render)
        if (prevTabRef.current !== tab && prevTabRef.current !== null) {
            const navbarOffset = 80;
            const doScroll = () => {
                try {
                    if (!pageTopRef.current) return;
                    // Obtener la posición del elemento
                    const elementTop = pageTopRef.current.getBoundingClientRect().top + window.pageYOffset;
                    // Scroll suave a la posición menos el offset del navbar
                    window.scrollTo({
                        top: elementTop - navbarOffset,
                        behavior: 'smooth'
                    });
                } catch (e) { }
            };
            const timer = setTimeout(doScroll, 50);
            prevTabRef.current = tab;
            return () => clearTimeout(timer);
        } else {
            prevTabRef.current = tab;
        }
    }, [tab]);


    const handleChange = (field) => (event) => {
        const value = event.target.value;
        setDirty(true);
        setReceiptConfig((prev) => ({ ...prev, [field]: value }));
    };

    const uploadAsset = async (file, key) => {
        if (!file) {
            console.error('ConfiguracionRecibos: No se proporcionó archivo');
            return null;
        }
        if (!fraccionamientoId) {
            console.error('ConfiguracionRecibos: fraccionamientoId no disponible');
            return null;
        }
        try {
            const storageRef = ref(storage, `recibos/${fraccionamientoId}/${key}_${Date.now()}_${file.name}`);
            console.log('ConfiguracionRecibos: Subiendo archivo a:', storageRef.fullPath);
            const snap = await uploadBytes(storageRef, file);
            console.log('ConfiguracionRecibos: Archivo subido exitosamente');
            const downloadUrl = await getDownloadURL(snap.ref);
            console.log('ConfiguracionRecibos: URL obtenida:', downloadUrl);
            return downloadUrl;
        } catch (error) {
            console.error('ConfiguracionRecibos: Error en uploadAsset:', error);
            throw error;
        }
    };

    const handleFile = (field) => async (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            console.warn('ConfiguracionRecibos: No se seleccionó archivo');
            return;
        }
        if (!fraccionamientoId) {
            showNotification('Error: No se pudo identificar el fraccionamiento. Recarga la página.', 'error');
            console.error('ConfiguracionRecibos: fraccionamientoId no disponible al intentar subir archivo');
            return;
        }
        try {
            setSaving(true);
            console.log(`ConfiguracionRecibos: Iniciando carga de ${field}...`);
            const url = await uploadAsset(file, field);
            if (url) {
                console.log(`ConfiguracionRecibos: URL obtenida para ${field}:`, url);
                // Actualizar estado local
                setReceiptConfig((prev) => ({ ...prev, [`${field}Url`]: url }));
                setDirty(true);

                // Guardar automáticamente en Firestore después de subir
                try {
                    const refFracc = doc(db, 'fraccionamientos', fraccionamientoId);
                    await setDoc(refFracc, {
                        configuracionRecibos: {
                            ...receiptConfig,
                            [`${field}Url`]: url,
                        },
                    }, { merge: true });
                    console.log(`ConfiguracionRecibos: ${field}Url guardado en Firestore:`, url);
                    showNotification(`Archivo cargado y guardado correctamente.`, 'success');
                } catch (saveError) {
                    console.error('ConfiguracionRecibos: Error guardando URL en Firestore:', saveError);
                    // Aún mostramos éxito porque el archivo se subió, pero advertimos que no se guardó la URL
                    showNotification('Archivo cargado, pero hubo un problema al guardar la URL. Intenta guardar manualmente.', 'warning');
                }
            } else {
                console.error(`ConfiguracionRecibos: No se obtuvo URL para ${field}`);
                showNotification('No se pudo obtener la URL del archivo. Intenta de nuevo.', 'error');
            }
        } catch (error) {
            console.error(`ConfiguracionRecibos: Error al cargar ${field}:`, error);
            const errorMessage = error?.message || 'Error desconocido al cargar el archivo';
            showNotification(`No se pudo cargar el archivo: ${errorMessage}`, 'error');
        } finally {
            setSaving(false);
            // Limpiar el input para permitir subir el mismo archivo de nuevo si es necesario
            event.target.value = '';
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!fraccionamientoId) return;
        try {
            setSaving(true);
            const refFracc = doc(db, 'fraccionamientos', fraccionamientoId);
            const listToSave = asegurarConceptosCategoriaA(conceptos);
            const conceptosObject = listToSave.reduce((acc, label, index) => {
                acc[`concepto_${index + 1}`] = label;
                return acc;
            }, {});

            // Obtener todos los conceptos de egreso derivados de las categorías jerárquicas
            const todosConceptosEgreso = obtenerTodosConceptosEgreso(categoriasEgreso);
            const conceptosEgresoObject = todosConceptosEgreso.reduce((acc, label, index) => {
                acc[`concepto_egreso_${index + 1}`] = label;
                return acc;
            }, {});

            await setDoc(refFracc, {
                configuracionRecibos: {
                    ...receiptConfig,
                    conceptos: conceptosObject,
                    conceptosList: listToSave,
                    conceptosEgreso: conceptosEgresoObject,
                    conceptosEgresoList: reorderOtrosLast(todosConceptosEgreso),
                    conceptosPrices: receiptConfig.conceptosPrices || {}, // Guardar precios fijos
                    categoriasEgreso: categoriasEgreso, // <--- Nueva estructura de bolsas jerárquicas
                },
            }, { merge: true });

            setConceptosEgreso(reorderOtrosLast(todosConceptosEgreso));
            setLastSavedAt(new Date());
            setDirty(false);
            showNotification('Configuración de recibos y bolsas de egresos guardada.', 'success');
        } catch (error) {
            console.error('ConfiguracionRecibos: no se pudieron guardar los ajustes', error);
            showNotification('No pudimos guardar la configuración. Intenta nuevamente.', 'error');
        } finally {
            setSaving(false);
        }
    };

    // Funciones para gestión de Categorías y Bolsas de Egresos
    const handleOpenCrearCategoria = () => {
        setCategoriaEditando(null);
        setCategoriaForm({
            id: `cat_${Date.now()}`,
            nombre: '',
            descripcion: '',
            color: '#10b981',
            conceptosIngresoAsociados: ['Cuota de Mantenimiento'],
            esBolsaProyecto: false,
            cuotaExtraAsociadaId: null,
            montoCuotaExtra: '',
            frecuenciaCuotaExtra: 'monthly',
            mesInicioCuotaExtra: new Date().getMonth() + 1,
            duracionMesesCuotaExtra: 6,
            congelacion: {
                habilitada: false,
                periodicidad: 'mensual',
                saldoCongeladoAcumulado: 0
            }
        });
        setShowModalCategoria(true);
    };

    const handleOpenEditarCategoria = (cat) => {
        setCategoriaEditando(cat);
        setCategoriaForm({
            id: cat.id,
            nombre: cat.nombre,
            descripcion: cat.descripcion || '',
            color: cat.color || '#10b981',
            conceptosIngresoAsociados: [...(cat.conceptosIngresoAsociados || [])],
            esBolsaProyecto: Boolean(cat.esBolsaProyecto),
            cuotaExtraAsociadaId: cat.cuotaExtraAsociadaId || null,
            montoCuotaExtra: cat.montoCuotaExtra != null ? cat.montoCuotaExtra : '',
            frecuenciaCuotaExtra: cat.frecuenciaCuotaExtra || 'monthly',
            mesInicioCuotaExtra: cat.mesInicioCuotaExtra !== undefined ? Number(cat.mesInicioCuotaExtra) : new Date().getMonth() + 1,
            duracionMesesCuotaExtra: cat.duracionMesesCuotaExtra != null ? Number(cat.duracionMesesCuotaExtra) : 6,
            congelacion: {
                habilitada: Boolean(cat.congelacion?.habilitada),
                periodicidad: cat.congelacion?.periodicidad || 'mensual',
                saldoCongeladoAcumulado: Number(cat.congelacion?.saldoCongeladoAcumulado || 0)
            }
        });
        setShowModalCategoria(true);
    };

    // Función reutilizable para persistir las categorías y sincronizar conceptos de egreso en Firestore inmediatamente
    const guardarCategoriasEnFirestore = async (nuevasCategorias) => {
        if (!fraccionamientoId) return false;
        try {
            setSaving(true);
            const refFracc = doc(db, 'fraccionamientos', fraccionamientoId);
            const listToSave = asegurarConceptosCategoriaA(conceptos);
            const conceptosObject = listToSave.reduce((acc, label, index) => {
                acc[`concepto_${index + 1}`] = label;
                return acc;
            }, {});

            // Obtener todos los conceptos de egreso derivados de las categorías jerárquicas
            const todosConceptosEgreso = obtenerTodosConceptosEgreso(nuevasCategorias);
            const conceptosEgresoObject = todosConceptosEgreso.reduce((acc, label, index) => {
                acc[`concepto_egreso_${index + 1}`] = label;
                return acc;
            }, {});

            await setDoc(refFracc, {
                configuracionRecibos: {
                    ...receiptConfig,
                    conceptos: conceptosObject,
                    conceptosList: listToSave,
                    conceptosEgreso: conceptosEgresoObject,
                    conceptosEgresoList: reorderOtrosLast(todosConceptosEgreso),
                    conceptosPrices: receiptConfig.conceptosPrices || {},
                    categoriasEgreso: nuevasCategorias,
                },
            }, { merge: true });

            setCategoriasEgreso(nuevasCategorias);
            setConceptosEgreso(reorderOtrosLast(todosConceptosEgreso));
            setLastSavedAt(new Date());
            setDirty(false);
            return true;
        } catch (error) {
            console.error('ConfiguracionRecibos: Error guardando categorias de egreso en Firestore:', error);
            showNotification('Error al guardar en la base de datos. Intente nuevamente.', 'error');
            return false;
        } finally {
            setSaving(false);
        }
    };

    const handleGuardarCategoriaModal = async () => {
        const nombreLimpio = categoriaForm.nombre.trim();
        if (!nombreLimpio) {
            showNotification('El nombre de la categoría es obligatorio.', 'warning');
            return;
        }

        if (categoriaForm.esBolsaProyecto) {
            const montoNum = Number(categoriaForm.montoCuotaExtra);
            if (!categoriaForm.montoCuotaExtra || isNaN(montoNum) || montoNum <= 0) {
                showNotification('Ingresa un monto por vivienda válido mayor a 0 para la cuota extraordinaria del proyecto.', 'warning');
                return;
            }
            const duracionNum = parseInt(categoriaForm.duracionMesesCuotaExtra, 10);
            if (isNaN(duracionNum) || duracionNum < 1) {
                showNotification('La duración del proyecto debe ser de al menos 1 mes.', 'warning');
                return;
            }
        }

        try {
            setSaving(true);
            const refFracc = doc(db, 'fraccionamientos', fraccionamientoId);
            const snapFracc = await getDoc(refFracc);
            const fraccData = snapFracc.exists() ? snapFracc.data() : {};
            const configFin = fraccData.configuracionFinanciera || {};
            let cuotaExtras = [...(configFin.cuotaExtras || [])];

            let cuotaExtraId = categoriaForm.cuotaExtraAsociadaId;

            // Si es Bolsa de Proyecto, vincular/crear Cuota Extraordinaria en configuracionFinanciera
            if (categoriaForm.esBolsaProyecto) {
                if (!cuotaExtraId) {
                    cuotaExtraId = `EXT_${Date.now()}`;
                }
                const extraObj = {
                    id: cuotaExtraId,
                    descripcion: nombreLimpio,
                    monto: Number(categoriaForm.montoCuotaExtra),
                    frecuencia: categoriaForm.frecuenciaCuotaExtra || 'monthly',
                    mesInicio: Number(categoriaForm.mesInicioCuotaExtra || new Date().getMonth() + 1),
                    duracionMeses: Number(categoriaForm.duracionMesesCuotaExtra || 6),
                    activa: true,
                    origen: 'bolsa_proyecto'
                };

                const idxExistente = cuotaExtras.findIndex(c => String(c.id) === String(cuotaExtraId) || c.descripcion?.toLowerCase().trim() === nombreLimpio.toLowerCase());
                if (idxExistente >= 0) {
                    cuotaExtras[idxExistente] = { ...cuotaExtras[idxExistente], ...extraObj };
                } else {
                    cuotaExtras.push(extraObj);
                }

                // Sincronizar cuotaExtras y forzar trigger con lastSyncExtra
                await setDoc(refFracc, {
                    configuracionFinanciera: {
                        ...configFin,
                        cuotaExtras,
                        lastSyncExtra: Date.now()
                    }
                }, { merge: true });
            } else {
                // Detección inteligente: si coincide con una cuota extraordinaria ya creada en configuracionFinanciera
                const extraExistente = cuotaExtras.find(c =>
                    c.descripcion?.toLowerCase().trim() === nombreLimpio.toLowerCase() ||
                    (categoriaForm.conceptosIngresoAsociados || []).some(ci => ci?.toLowerCase().trim() === c.descripcion?.toLowerCase().trim())
                );
                if (extraExistente) {
                    cuotaExtraId = extraExistente.id;
                    categoriaForm.esBolsaProyecto = true;
                    categoriaForm.montoCuotaExtra = extraExistente.monto || categoriaForm.montoCuotaExtra;
                    categoriaForm.frecuenciaCuotaExtra = extraExistente.frecuencia || categoriaForm.frecuenciaCuotaExtra;
                }
            }

            const conceptosAsociados = categoriaForm.esBolsaProyecto
                ? ['Cuota Extraordinaria', nombreLimpio]
                : (categoriaForm.conceptosIngresoAsociados.length > 0 ? categoriaForm.conceptosIngresoAsociados : ['Cuota de Mantenimiento']);

            let updatedCats = [];
            let mensaje = '';
            if (categoriaEditando) {
                updatedCats = categoriasEgreso.map(c => c.id === categoriaEditando.id ? {
                    ...c,
                    nombre: nombreLimpio,
                    descripcion: categoriaForm.descripcion.trim(),
                    color: categoriaForm.color,
                    esBolsaProyecto: Boolean(categoriaForm.esBolsaProyecto),
                    cuotaExtraAsociadaId: categoriaForm.esBolsaProyecto ? cuotaExtraId : null,
                    montoCuotaExtra: categoriaForm.esBolsaProyecto ? Number(categoriaForm.montoCuotaExtra) : null,
                    frecuenciaCuotaExtra: categoriaForm.esBolsaProyecto ? categoriaForm.frecuenciaCuotaExtra : 'monthly',
                    mesInicioCuotaExtra: categoriaForm.esBolsaProyecto ? Number(categoriaForm.mesInicioCuotaExtra) : 1,
                    duracionMesesCuotaExtra: categoriaForm.esBolsaProyecto ? Number(categoriaForm.duracionMesesCuotaExtra) : 6,
                    congelacion: {
                        habilitada: Boolean(categoriaForm.congelacion?.habilitada),
                        periodicidad: categoriaForm.congelacion?.periodicidad || 'mensual',
                        saldoCongeladoAcumulado: Number(categoriaForm.congelacion?.saldoCongeladoAcumulado || 0),
                        historico: Array.isArray(c?.congelacion?.historico) ? c.congelacion.historico : []
                    },
                    conceptosIngresoAsociados: conceptosAsociados
                } : c);
                mensaje = `Categoría "${nombreLimpio}" actualizada y sincronizada.`;
            } else {
                const nueva = {
                    id: `cat_${Date.now()}`,
                    nombre: nombreLimpio,
                    descripcion: categoriaForm.descripcion.trim(),
                    color: categoriaForm.color,
                    esBolsaProyecto: Boolean(categoriaForm.esBolsaProyecto),
                    cuotaExtraAsociadaId: categoriaForm.esBolsaProyecto ? cuotaExtraId : null,
                    montoCuotaExtra: categoriaForm.esBolsaProyecto ? Number(categoriaForm.montoCuotaExtra) : null,
                    frecuenciaCuotaExtra: categoriaForm.esBolsaProyecto ? categoriaForm.frecuenciaCuotaExtra : 'monthly',
                    mesInicioCuotaExtra: categoriaForm.esBolsaProyecto ? Number(categoriaForm.mesInicioCuotaExtra) : 1,
                    duracionMesesCuotaExtra: categoriaForm.esBolsaProyecto ? Number(categoriaForm.duracionMesesCuotaExtra) : 6,
                    congelacion: {
                        habilitada: Boolean(categoriaForm.congelacion?.habilitada),
                        periodicidad: categoriaForm.congelacion?.periodicidad || 'mensual',
                        saldoCongeladoAcumulado: Number(categoriaForm.congelacion?.saldoCongeladoAcumulado || 0),
                        historico: []
                    },
                    conceptosIngresoAsociados: conceptosAsociados,
                    conceptos: []
                };
                updatedCats = [...categoriasEgreso, nueva];
                mensaje = `Categoría "${nombreLimpio}" creada y sincronizada con éxito.`;
            }

            const ok = await guardarCategoriasEnFirestore(updatedCats);
            if (ok) {
                setShowModalCategoria(false);
                showNotification(mensaje, 'success');
            }
        } catch (err) {
            console.error('Error guardando categoría y cuota de proyecto:', err);
            showNotification('Error al guardar en la base de datos.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleConfirmarEliminarCategoria = async () => {
        if (!categoriaParaEliminar) return;
        const nombreEliminado = categoriaParaEliminar.nombre;
        const updatedCats = categoriasEgreso.filter(c => c.id !== categoriaParaEliminar.id);
        setCategoriaParaEliminar(null);
        const ok = await guardarCategoriasEnFirestore(updatedCats);
        if (ok) {
            showNotification(`Categoría "${nombreEliminado}" eliminada y cambios guardados.`, 'success');
        }
    };

    const handleAddConceptoEspecifico = async (catId) => {
        const texto = (nuevoConceptoTextoPorCat[catId] || '').trim();
        const tipo = nuevoConceptoTipoPorCat[catId] || 'Variable';
        if (!texto) {
            showNotification('Escribe el nombre del concepto que deseas agregar a esta categoría.', 'warning');
            const inputEl = document.getElementById(`input-concepto-${catId}`);
            if (inputEl) inputEl.focus();
            return;
        }

        const targetCat = categoriasEgreso.find(c => c.id === catId);
        if (targetCat) {
            const conceptosActuales = targetCat.conceptos || [];
            if (conceptosActuales.some(c => (typeof c === 'string' ? c : c.nombre).toLowerCase() === texto.toLowerCase())) {
                showNotification(`El concepto "${texto}" ya existe en esta categoría.`, 'warning');
                return;
            }
        }

        const updatedCats = categoriasEgreso.map(cat => {
            if (cat.id !== catId) return cat;
            const conceptosActuales = cat.conceptos || [];
            return {
                ...cat,
                conceptos: [
                    ...conceptosActuales,
                    { id: `c_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`, nombre: texto, tipoGasto: tipo }
                ]
            };
        });

        setNuevoConceptoTextoPorCat(prev => ({ ...prev, [catId]: '' }));
        const ok = await guardarCategoriasEnFirestore(updatedCats);
        if (ok) {
            showNotification(`Concepto "${texto}" agregado y guardado.`, 'success');
        }
    };

    const handleToggleTipoGastoConcepto = async (catId, conceptId) => {
        const updatedCats = categoriasEgreso.map(cat => {
            if (cat.id !== catId) return cat;
            return {
                ...cat,
                conceptos: (cat.conceptos || []).map(c => {
                    if (c.id === conceptId) {
                        return { ...c, tipoGasto: c.tipoGasto === 'Fijo' ? 'Variable' : 'Fijo' };
                    }
                    return c;
                })
            };
        });
        await guardarCategoriasEnFirestore(updatedCats);
    };

    const handleDeleteConceptoEspecifico = async (catId, conceptId) => {
        const updatedCats = categoriasEgreso.map(cat => {
            if (cat.id !== catId) return cat;
            return {
                ...cat,
                conceptos: (cat.conceptos || []).filter(c => c.id !== conceptId)
            };
        });
        const ok = await guardarCategoriasEnFirestore(updatedCats);
        if (ok) {
            showNotification('Concepto de egreso eliminado y cambios guardados.', 'success');
        }
    };

    const handleCargarPlantillaOficialAgave = async () => {
        setConfirmPlantillaOpen(false);
        const ok = await guardarCategoriasEnFirestore(PLANTILLA_EGRESOS_AGAVE_V);
        if (ok) {
            showNotification('Plantilla oficial de Agave V (6 Bolsas del Diario de Banco) cargada y guardada en la base de datos.', 'success');
        }
    };

    const handleTabClick = (nextTab) => {
        if (nextTab === tab) return;
        if (dirty) {
            setPendingTab(nextTab);
            setShowUnsavedPrompt(true);
            return;
        }
        setTab(nextTab);
    };

    const handleAddConcept = () => {
        const trimmed = newConcept.trim();
        if (!trimmed) return;
        // En lugar de agregar directamente, abrir modal de precio fijo
        setPendingConcept(trimmed);
        setNewConcept('');
        setShowPriceModal(true);
    };

    // Guardar concepto con o sin precio fijo
    const handleSaveConceptWithPrice = (withPrice) => {
        if (!pendingConcept) return;

        // Si tiene precio fijo, guardarlo en la configuración
        if (withPrice && conceptPrice && parseFloat(conceptPrice) > 0) {
            const updatedPrices = {
                ...(receiptConfig.conceptosPrices || {}),
                [pendingConcept]: parseFloat(conceptPrice)
            };

            setReceiptConfig(prev => ({
                ...prev,
                conceptosPrices: updatedPrices
            }));
        }

        // Agregar el concepto a la lista
        setConceptos((prev) => reorderOtrosLast([...prev, pendingConcept]));

        // Limpiar estados
        setPendingConcept('');
        setConceptPrice('');
        setShowPriceModal(false);
        setDirty(true);
    };

    const handleRemoveConcept = (label) => {
        const normalized = normalizeLabel(label);
        if (BLOCKED_CONCEPTS.has(normalized)) {
            showNotification(`El concepto "${label}" es obligatorio y no se puede eliminar.`, 'warning');
            return;
        }
        setConfirmDeleteLabel(normalized);
        setConfirmDeleteOpen(true);
    };

    const handleAddConceptEgreso = () => {
        const trimmed = newConceptEgreso.trim();
        if (!trimmed) return;
        setConceptosEgreso((prev) => reorderOtrosLast([...prev, trimmed]));
        setNewConceptEgreso('');
        setDirty(true);
    };

    const handleRemoveConceptEgreso = (label) => {
        setConfirmDeleteEgresoLabel(label);
        setConfirmDeleteEgresoOpen(true);
    };

    const formatoFecha = (fecha) => receiptConfig.fechaFormato === 'larga'
        ? fecha.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })
        : fecha.toLocaleDateString('es-MX');

    // Convierte URL privada de Storage (con token) a URL pública (solo ?alt=media)
    // Equivalente a toPublicStorageUrl en Cloud Functions
    const toPublicUrl = (url) => {
        if (!url || typeof url !== 'string') return '';
        try {
            const p = new URL(url);
            if (!p.hostname.includes('firebasestorage')) return url;
            p.search = '?alt=media';
            return p.toString();
        } catch {
            return url.split('?')[0] + '?alt=media';
        }
    };

    const folioSerie = ((receiptConfig.folioSerie || 'R').replace(/[^A-Za-z]/g, '')) || 'R';
    const folioConsec = String(receiptConfig.folioConsecutivo || 1).padStart(6, '0');
    const folioPreview = `${folioSerie}${folioConsec}`;
    const folioSerieEgreso = ((receiptConfig.folioSerieEgreso || 'EGR').replace(/[^A-Za-z]/g, '')) || 'EGR';
    const folioConsecEgreso = String(receiptConfig.folioConsecutivoEgreso || 1).padStart(6, '0');
    const folioPreviewEgreso = `${folioSerieEgreso}${folioConsecEgreso}`;
    const fechaPagoPreview = new Date();
    const fechaEmisionPreview = new Date();

    const previewConceptos = (conceptos.slice(0, 2).length ? conceptos.slice(0, 2) : ['Cuota de Mantenimiento', 'Adeudos anteriores']);
    const previewImportes = previewConceptos.map(() => 1500);
    const totalPreview = previewImportes.reduce((sum, v) => sum + v, 0);
    const previewConceptosEgreso = (conceptosEgreso.slice(0, 2).length ? conceptosEgreso.slice(0, 2) : ['Mantenimiento', 'Servicios Públicos']);
    const previewImportesEgreso = previewConceptosEgreso.map(() => 1200);
    const totalPreviewEgreso = previewImportesEgreso.reduce((sum, v) => sum + v, 0);
    const previewMetodoPagoEgreso = 'Tarjeta de Débito';
    const previewPrioridadEgreso = 'Media';

    const numeroALetras = (num = 0) => {
        const unidades = ['CERO', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
        const decenas = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISÉIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
        const tens = ['', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
        const centenas = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];

        if (num === 0) return 'CERO';
        if (num === 100) return 'CIEN';

        const toWords = (n) => {
            let words = '';
            const c = Math.floor(n / 100);
            const d = Math.floor((n % 100) / 10);
            const u = n % 10;
            if (c) words += `${centenas[c]} `;
            if (d === 1) {
                words += `${decenas[u]} `;
            } else if (d === 2) {
                words += u === 0 ? 'VEINTE ' : `VEINTI${unidades[u].toLowerCase()} `;
            } else if (d > 2) {
                words += tens[d];
                if (u) words += ` Y ${unidades[u]}`;
                words += ' ';
            } else if (u) {
                words += `${unidades[u]} `;
            }
            return words.trim();
        };

        const miles = Math.floor(num / 1000);
        const resto = num % 1000;
        let res = '';
        if (miles) {
            if (miles === 1) res += 'MIL';
            else res += `${toWords(miles)} MIL`;
        }
        if (resto) {
            res += res ? ' ' : '';
            res += toWords(resto);
        }
        return res.trim();
    };

    const monedaEtiqueta = (receiptConfig.moneda || 'MXN').toUpperCase() === 'USD'
        ? 'DÓLARES ESTADOUNIDENSES'
        : 'PESOS MEXICANOS';
    const totalEnLetras = `${numeroALetras(totalPreview)} ${monedaEtiqueta} 00/100`;
    const totalEnLetrasEgreso = `${numeroALetras(totalPreviewEgreso)} ${monedaEtiqueta} 00/100`;

    return (
        <div ref={pageTopRef} className="recibos-page premium-view">
            <header className="recibos-header-premium">
                <div className="receipts-title-section">
                    <h1><FaFileInvoiceDollar /> Configuración de Recibos</h1>
                    <p>Personaliza el encabezado, datos fiscales, conceptos de pago y sellos de validez para los recibos.</p>
                </div>
                <div className="receipts-actions">
                    <Link to="/configuraciones" className="back-btn-premium">
                        <FaArrowLeft /> Volver a Configuraciones
                    </Link>
                </div>
            </header>

            <form onSubmit={handleSubmit} className="recibos-form-premium">
                <div className="receipts-sticky-tabs">
                    <div className="receipts-tabs-scroll">
                        <button type="button" className={`receipt-tab ${tab === 0 ? 'active' : ''}`} onClick={() => handleTabClick(0)}>1. Información General</button>
                        <button type="button" className={`receipt-tab ${tab === 1 ? 'active' : ''}`} onClick={() => handleTabClick(1)}>2. Datos de Facturación</button>
                        <button type="button" className={`receipt-tab ${tab === 2 ? 'active' : ''}`} onClick={() => handleTabClick(2)}>3. Conceptos de Pago</button>
                        <button type="button" className={`receipt-tab ${tab === 3 ? 'active' : ''}`} onClick={() => handleTabClick(3)}>4. Conceptos de Egresos</button>
                        <button type="button" className={`receipt-tab ${tab === 4 ? 'active' : ''}`} onClick={() => handleTabClick(4)}>5. Vista previa</button>
                    </div>
                </div>

                <div className="receipts-content-area">
                    {/* TAB 0: Información General */}
                    {tab === 0 && (
                        <div className="receipt-tab-inner">
                            <div className="section-banner-premium">
                                <div className="banner-index">1</div>
                                <div className="banner-info">
                                    <h3>Información General</h3>
                                    <p>Logo, datos de contacto y formato de fecha</p>
                                </div>
                            </div>

                            <div className="receipts-settings-grid">
                                <div className="settings-card-premium">
                                    <div className="card-header-premium">
                                        <h4>Identidad Visual</h4>
                                    </div>
                                    <div className="form-group-premium">
                                        <label>Logo del condominio <FaQuestionCircle className="help-icon-premium" title="Aparece en el encabezado del recibo." /></label>
                                        <div className="upload-wrapper-premium">
                                            <label className="btn-upload-premium">
                                                <FaUpload /> Subir logo
                                                <input type="file" accept="image/*" onChange={handleFile('logo')} disabled={saving} />
                                            </label>
                                            {receiptConfig.logoUrl && (
                                                <div className="preview-miniature">
                                                    <img src={toPublicUrl(receiptConfig.logoUrl)} alt="Logo" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="size-controls-premium">
                                            <div className="size-input">
                                                <label>Ancho (px)</label>
                                                <input
                                                    type="number"
                                                    className="modern-input"
                                                    value={receiptConfig.logoWidth}
                                                    onChange={handleChange('logoWidth')}
                                                    disabled={saving}
                                                />
                                            </div>
                                            <div className="size-input">
                                                <label>Alto (px)</label>
                                                <input
                                                    type="number"
                                                    className="modern-input"
                                                    value={receiptConfig.logoHeight}
                                                    onChange={handleChange('logoHeight')}
                                                    disabled={saving}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="form-group-premium">
                                        <label>Sello / Firma Digital <FaQuestionCircle className="help-icon-premium" title="Aparece al calce del recibo." /></label>
                                        <div className="upload-wrapper-premium">
                                            <label className="btn-upload-premium secondary">
                                                <FaUpload /> Subir Sello
                                                <input type="file" accept="image/*" onChange={handleFile('sello')} disabled={saving} />
                                            </label>
                                            {receiptConfig.selloUrl && (
                                                <div className="preview-miniature">
                                                    <img src={toPublicUrl(receiptConfig.selloUrl)} alt="Sello" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="size-controls-premium">
                                            <div className="size-input">
                                                <label>Ancho (px)</label>
                                                <input
                                                    type="number"
                                                    className="modern-input"
                                                    value={receiptConfig.selloWidth}
                                                    onChange={handleChange('selloWidth')}
                                                    disabled={saving}
                                                />
                                            </div>
                                            <div className="size-input">
                                                <label>Alto (px)</label>
                                                <input
                                                    type="number"
                                                    className="modern-input"
                                                    value={receiptConfig.selloHeight}
                                                    onChange={handleChange('selloHeight')}
                                                    disabled={saving}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="settings-card-premium">
                                    <div className="card-header-premium">
                                        <h4>Nomenclatura y Fecha</h4>
                                    </div>
                                    <div className="form-grid-premium">
                                        <div className="form-group-premium">
                                            <label>Nombre del Condominio</label>
                                            <input
                                                type="text"
                                                className="modern-input"
                                                placeholder="Ej. Residencial Las Flores"
                                                value={receiptConfig.condominioNombre}
                                                onChange={handleChange('condominioNombre')}
                                                disabled={saving}
                                            />
                                        </div>
                                        <div className="form-group-premium">
                                            <label>Nombre del Administrador</label>
                                            <input
                                                type="text"
                                                className="modern-input"
                                                placeholder="Ej. Administradora Central"
                                                value={receiptConfig.administradorNombre}
                                                onChange={handleChange('administradorNombre')}
                                                disabled={saving}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group-premium">
                                        <label>Formato de Fecha</label>
                                        <select
                                            className="modern-select"
                                            value={receiptConfig.fechaFormato || 'corta'}
                                            onChange={handleChange('fechaFormato')}
                                            disabled={saving}
                                        >
                                            <option value="corta">Corta (Ej. 15/01/2024)</option>
                                            <option value="larga">Larga (Ej. 15 de enero de 2024)</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="settings-card-premium full-width-card mt-24">
                                <div className="card-header-premium">
                                    <h4>Ubicación y Contacto</h4>
                                </div>
                                <div className="form-grid-premium">
                                    <div className="form-group-premium">
                                        <label>Dirección del Condominio</label>
                                        <textarea
                                            className="modern-textarea"
                                            placeholder="Av. Paseo de los Alamos #123..."
                                            value={receiptConfig.condominioDireccion}
                                            onChange={handleChange('condominioDireccion')}
                                            disabled={saving}
                                        />
                                    </div>
                                    <div className="form-group-premium">
                                        <label>Dirección del Administrador</label>
                                        <textarea
                                            className="modern-textarea"
                                            placeholder="Calle Falsa #123, Local 4..."
                                            value={receiptConfig.adminDireccion}
                                            onChange={handleChange('adminDireccion')}
                                            disabled={saving}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TAB 1: Datos de Facturación */}
                    {tab === 1 && (
                        <div className="receipt-tab-inner">
                            <div className="section-banner-premium">
                                <div className="banner-index">2</div>
                                <div className="banner-info">
                                    <h3>Datos de Facturación</h3>
                                    <p>RFC, razón social, moneda y folios</p>
                                </div>
                            </div>
                            <div className="receipts-settings-grid">
                                <div className="settings-card-premium">
                                    <div className="card-header-premium">
                                        <h4>Información Fiscal</h4>
                                    </div>
                                    <div className="form-grid-premium">
                                        <div className="form-group-premium">
                                            <label>Razón Social</label>
                                            <input
                                                type="text"
                                                className="modern-input"
                                                value={receiptConfig.razonSocial}
                                                onChange={handleChange('razonSocial')}
                                                disabled={saving}
                                            />
                                        </div>
                                        <div className="form-group-premium">
                                            <label>RFC</label>
                                            <input
                                                type="text"
                                                className="modern-input"
                                                value={receiptConfig.rfc}
                                                onChange={handleChange('rfc')}
                                                disabled={saving}
                                                placeholder="Ej: ABC123456XYZ"
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group-premium">
                                        <label>Moneda Local</label>
                                        <input
                                            type="text"
                                            className="modern-input"
                                            value={receiptConfig.moneda}
                                            onChange={handleChange('moneda')}
                                            disabled={saving}
                                            placeholder="MXN"
                                            maxLength="3"
                                        />
                                    </div>
                                </div>
                                <div className="settings-card-premium">
                                    <div className="card-header-premium">
                                        <h4>Folios y Numeración</h4>
                                    </div>
                                    <div className="folio-grid-premium">
                                        <div className="form-group-premium">
                                            <label>Prefijo Ticket (Ingresos)</label>
                                            <input
                                                type="text"
                                                className="modern-input"
                                                value={receiptConfig.folioSerie}
                                                onChange={handleChange('folioSerie')}
                                                disabled={saving}
                                                placeholder="Ex: REC"
                                            />
                                        </div>
                                        <div className="form-group-premium">
                                            <label>Prefijo Egreso</label>
                                            <input
                                                type="text"
                                                className="modern-input"
                                                value={receiptConfig.folioSerieEgreso || ''}
                                                onChange={handleChange('folioSerieEgreso')}
                                                disabled={saving}
                                                placeholder="Ex: EGR"
                                            />
                                        </div>
                                        <div className="form-group-premium">
                                            <label>Siguiente Ticket</label>
                                            <input
                                                type="number"
                                                className="modern-input"
                                                value={receiptConfig.folioConsecutivo}
                                                onChange={handleChange('folioConsecutivo')}
                                                disabled={saving}
                                            />
                                        </div>
                                        <div className="form-group-premium">
                                            <label>Siguiente Egreso</label>
                                            <input
                                                type="number"
                                                className="modern-input"
                                                value={receiptConfig.folioConsecutivoEgreso || 1}
                                                onChange={handleChange('folioConsecutivoEgreso')}
                                                disabled={saving}
                                            />
                                        </div>
                                    </div>
                                    <p className="config-hint-premium">La numeración se incrementa automáticamente tras cada operación.</p>
                                </div>
                            </div>
                            <div className="settings-card-premium full-width-card mt-24">
                                <div className="card-header-premium">
                                    <h4>Datos de Facturación Completos</h4>
                                </div>
                                <div className="form-group-premium">
                                    <label>Información fiscal adicional <FaQuestionCircle className="help-icon-premium" title="Incluye regimen fiscal, correo y otros datos de facturación." /></label>
                                    <textarea
                                        className="modern-textarea"
                                        rows="3"
                                        value={receiptConfig.datosFacturacion}
                                        onChange={handleChange('datosFacturacion')}
                                        disabled={saving}
                                        placeholder="Régimen fiscal, correo de facturación, teléfono, etc."
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TAB 2: Conceptos de Pago */}
                    {tab === 2 && (() => {
                        const conceptosContables = conceptos.filter(c => esPartidaContableResidente(c));
                        const conceptosTesoreria = conceptos.filter(c => !esPartidaContableResidente(c));

                        return (
                            <div className="receipt-tab-inner">
                                <div className="section-banner-premium purple-gradient">
                                    <div className="banner-index">3</div>
                                    <div className="banner-info">
                                        <h3>Conceptos de Pago</h3>
                                        <p>Administra los conceptos divididos por su categoría contable y efecto en adeudos</p>
                                    </div>
                                </div>

                                <div className="settings-card-premium full-width-card" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                    {/* BLOQUE 1: LISTA BLANCA A - CONCEPTOS CONTABLES DE RESIDENTES */}
                                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{ fontSize: '1.25rem' }}>🛡️</span>
                                                <h4 style={{ margin: 0, color: '#1e3a8a', fontSize: '1.1rem', fontWeight: 700 }}>
                                                    Categoría A: Contabilidad de Residentes
                                                </h4>
                                            </div>
                                            <span style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '4px 10px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 700 }}>
                                                Afecta Adeudo y Saldo a Favor
                                            </span>
                                        </div>
                                        <p style={{ margin: '0 0 14px 0', fontSize: '0.85rem', color: '#475569', lineHeight: '1.4' }}>
                                            📌 <strong>Regla Inmutable:</strong> Estos conceptos amortizan los adeudos del residente (cuota ordinaria, adeudos anteriores, mora, cuotas extraordinarias, sanciones o agua) o generan saldo a favor en caso de anticipo.
                                        </p>
                                        <div className="concepts-list-modern">
                                            {conceptosContables.map((label) => (
                                                <div key={label} className="concept-chip-premium" style={{ borderLeft: '4px solid #2563eb' }}>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        {BLOCKED_CONCEPTS.has(label) && <FaLock style={{ color: '#64748b', fontSize: '0.75rem' }} title="Concepto protegido" />}
                                                        {label}
                                                        {receiptConfig.conceptosPrices?.[label] && (
                                                            <span className="price-badge">
                                                                ${receiptConfig.conceptosPrices[label].toFixed(2)}
                                                            </span>
                                                        )}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="chip-remove-btn"
                                                        onClick={() => handleRemoveConcept(label)}
                                                        disabled={saving || BLOCKED_CONCEPTS.has(label)}
                                                        title={BLOCKED_CONCEPTS.has(label) ? 'Concepto protegido por regla contable' : 'Eliminar concepto'}
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* BLOQUE 2: LISTA B - CONCEPTOS DE TESORERÍA DEL CONDOMINIO */}
                                    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '20px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{ fontSize: '1.25rem' }}>💼</span>
                                                <h4 style={{ margin: 0, color: '#92400e', fontSize: '1.1rem', fontWeight: 700 }}>
                                                    Categoría B: Ingresos de Tesorería del Fraccionamiento
                                                </h4>
                                            </div>
                                            <span style={{ background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', padding: '4px 10px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 700 }}>
                                                Ingreso Puro • NO Afecta Adeudos
                                            </span>
                                        </div>
                                        <p style={{ margin: '0 0 14px 0', fontSize: '0.85rem', color: '#78350f', lineHeight: '1.4' }}>
                                            💼 <strong>Blindaje Contable:</strong> Los pagos registrados bajo estos conceptos son ingresos directos para la administración (venta de llaves peatonales, tags vehiculares, amenidades, etc.). <strong>No disminuyen adeudos de cuotas ni generan saldo a favor para el residente.</strong>
                                        </p>
                                        <div className="concepts-list-modern">
                                            {conceptosTesoreria.length === 0 ? (
                                                <div style={{ fontSize: '0.85rem', color: '#a16207', fontStyle: 'italic', padding: '6px 0' }}>
                                                    No hay conceptos de tesorería registrados. Puedes agregar uno en la caja inferior.
                                                </div>
                                            ) : (
                                                conceptosTesoreria.map((label) => (
                                                    <div key={label} className="concept-chip-premium" style={{ borderLeft: '4px solid #f59e0b', background: '#fff' }}>
                                                        <span>
                                                            {label}
                                                            {receiptConfig.conceptosPrices?.[label] && (
                                                                <span className="price-badge" style={{ background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a' }}>
                                                                    ${receiptConfig.conceptosPrices[label].toFixed(2)}
                                                                </span>
                                                            )}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="chip-remove-btn"
                                                            onClick={() => handleRemoveConcept(label)}
                                                            disabled={saving}
                                                            title="Eliminar concepto de tesorería"
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>

                                    {/* CAJA PARA AGREGAR NUEVO CONCEPTO */}
                                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
                                        <h5 style={{ margin: '0 0 8px 0', color: '#1e293b', fontSize: '0.95rem', fontWeight: 700 }}>
                                            Agregar Nuevo Concepto
                                        </h5>
                                        <p style={{ margin: '0 0 14px 0', fontSize: '0.82rem', color: '#64748b' }}>
                                            💡 <em>Nota: Por protección contable del fraccionamiento, todo nuevo concepto personalizado se registra automáticamente como <strong>Ingreso de Tesorería (Categoría B)</strong> sin alterar adeudos de los colonos.</em>
                                        </p>
                                        <div className="concept-add-box-premium">
                                            <input
                                                type="text"
                                                className="modern-input"
                                                placeholder="Ej: Lave Peatonal, Tag vehicular, Renta salón..."
                                                value={newConcept}
                                                onChange={(e) => setNewConcept(e.target.value)}
                                                disabled={saving}
                                                onKeyPress={(e) => e.key === 'Enter' && handleAddConcept()}
                                            />
                                            <button
                                                type="button"
                                                className="btn-add-premium"
                                                onClick={handleAddConcept}
                                                disabled={saving || !newConcept.trim()}
                                            >
                                                + Agregar Concepto
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* TAB 3: Conceptos y Bolsas de Egresos */}
                    {tab === 3 && (
                        <div className="receipt-tab-inner">
                            <div className="section-banner-premium indigo-gradient">
                                <div className="banner-index">4</div>
                                <div className="banner-info">
                                    <h3>Bolsas y Categorías de Egresos</h3>
                                    <p>Organiza tus gastos por categorías, clasifícalos como Fijos o Variables y vincúlalos a las bolsas de ingresos correspondientes.</p>
                                </div>
                            </div>

                            {/* BARRA DE ACCIONES SUPERIOR */}
                            <div className="bolsas-top-actions-bar">
                                <div className="bolsas-summary-chips">
                                    <span className="bolsas-count-badge">
                                        <FaFolder style={{ marginRight: '6px' }} /> {categoriasEgreso.length} Categorías
                                    </span>
                                    <span className="bolsas-count-badge secondary">
                                        <FaTags style={{ marginRight: '6px' }} /> {obtenerTodosConceptosEgreso(categoriasEgreso).length} Conceptos Específicos
                                    </span>

                                    {/* INDICADOR INTERACTIVO DE CONCEPTOS DE INGRESO SIN ASIGNAR */}
                                    <div
                                        className="bolsa-unassigned-wrapper"
                                        onMouseEnter={() => setShowUnassignedPopover(true)}
                                        onMouseLeave={() => setShowUnassignedPopover(false)}
                                    >
                                        {conceptosSinAsignar.length > 0 ? (
                                            <button
                                                type="button"
                                                className="bolsas-count-badge warning"
                                                onClick={() => setShowUnassignedPopover(prev => !prev)}
                                                title="Pasa el cursor o pulsa para ver los conceptos de cobro no asignados a ninguna bolsa"
                                            >
                                                <FaExclamationTriangle style={{ marginRight: '6px', color: '#f59e0b' }} />
                                                {conceptosSinAsignar.length} {conceptosSinAsignar.length === 1 ? 'Concepto sin asignar' : 'Conceptos sin asignar'}
                                            </button>
                                        ) : (
                                            <span
                                                className="bolsas-count-badge success"
                                                title="Todos los conceptos de cobro están vinculados y fondean al menos una categoría"
                                            >
                                                <FaCheckCircle style={{ marginRight: '6px', color: '#10b981' }} />
                                                100% Ingresos Asignados
                                            </span>
                                        )}

                                        {showUnassignedPopover && (
                                            <div className={`bolsa-unassigned-popover ${conceptosSinAsignar.length === 0 ? 'success-mode' : ''}`}>
                                                {conceptosSinAsignar.length > 0 ? (
                                                    <>
                                                        <div className="bolsa-popover-header">
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                                                <FaExclamationTriangle className="bolsa-popover-icon-warning" />
                                                                <span className="bolsa-popover-title">
                                                                    Ingresos sin Bolsa ({conceptosSinAsignar.length})
                                                                </span>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="bolsa-popover-close-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setShowUnassignedPopover(false);
                                                                }}
                                                                title="Cerrar"
                                                            >
                                                                <FaTimes size={11} />
                                                            </button>
                                                        </div>
                                                        <p className="bolsa-popover-desc">
                                                            Ingresan a tesorería pero <strong>ninguna categoría de egreso puede gastarlos</strong> porque no están vinculados:
                                                        </p>
                                                        <div className="bolsa-popover-list">
                                                            {conceptosSinAsignar.map((item, idx) => (
                                                                <div key={idx} className="bolsa-popover-item">
                                                                    <span className="bolsa-popover-bullet">•</span>
                                                                    <span className="bolsa-popover-item-name">{item}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <div className="bolsa-popover-footer">
                                                            <FaInfoCircle size={12} style={{ marginRight: '5px', flexShrink: 0, marginTop: '2px' }} />
                                                            <span>Edita una categoría (ej. <em>Servicios</em> o <em>Proyecto</em>) y vincúlalos para fondear tus egresos.</span>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="bolsa-popover-header">
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                                                <FaCheckCircle className="bolsa-popover-icon-success" />
                                                                <span className="bolsa-popover-title">Cobertura Total de Ingresos</span>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="bolsa-popover-close-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setShowUnassignedPopover(false);
                                                                }}
                                                                title="Cerrar"
                                                            >
                                                                <FaTimes size={11} />
                                                            </button>
                                                        </div>
                                                        <p className="bolsa-popover-desc" style={{ marginBottom: 0 }}>
                                                            Todos tus conceptos de cobro están vinculados y fondean al menos una categoría de egreso.
                                                        </p>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="bolsas-buttons-group">
                                    <button
                                        type="button"
                                        className="btn-zen-magic"
                                        onClick={() => setConfirmPlantillaOpen(true)}
                                        title="Cargar las 6 bolsas y categorías del Diario de Banco de Agave Azul V"
                                    >
                                        <FaMagic style={{ marginRight: '6px' }} /> Plantilla Oficial Agave V
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-zen-primary"
                                        onClick={handleOpenCrearCategoria}
                                    >
                                        <FaFolderPlus style={{ marginRight: '6px' }} /> + Nueva Categoría
                                    </button>
                                </div>
                            </div>

                            {/* NOTA EXPLICATIVA ZEN */}
                            <div className="bolsas-info-box-zen">
                                <FaInfoCircle className="bolsas-info-icon" />
                                <div className="bolsas-info-text">
                                    <strong>Control de Bolsas y Conciliación Financiera:</strong>
                                    <span>
                                        Cada categoría define conceptos específicos clasificados como <strong>Fijos</strong> (servicios y suministros recurrentes) o <strong>Variables</strong> (insumos, reparaciones y proyectos). Los gastos registrados en cada categoría se restarán automáticamente de los <strong>Ingresos que la fondean</strong> para calcular el balance neto de cada bolsa sin contaminar otros fondos.
                                    </span>
                                </div>
                            </div>

                            {/* GRID DE CATEGORÍAS */}
                            {categoriasEgreso.length === 0 ? (
                                <div className="bolsas-empty-card">
                                    <FaFolderPlus className="bolsas-empty-icon" />
                                    <h4>No hay categorías de egreso configuradas</h4>
                                    <p>Crea tu primera categoría o carga la plantilla oficial de Agave Azul V con un solo clic.</p>
                                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '16px' }}>
                                        <button
                                            type="button"
                                            className="btn-zen-magic"
                                            onClick={() => setConfirmPlantillaOpen(true)}
                                        >
                                            <FaMagic style={{ marginRight: '6px' }} /> Cargar Plantilla Agave V
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-zen-primary"
                                            onClick={handleOpenCrearCategoria}
                                        >
                                            <FaFolderPlus style={{ marginRight: '6px' }} /> + Crear Categoría
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="bolsas-categories-grid">
                                    {categoriasEgreso.map((cat) => {
                                        const conceptosList = cat.conceptos || [];
                                        const nuevoTexto = nuevoConceptoTextoPorCat[cat.id] || '';
                                        const nuevoTipo = nuevoConceptoTipoPorCat[cat.id] || 'Variable';

                                        return (
                                            <div
                                                key={cat.id}
                                                className="bolsa-category-card"
                                                style={{ borderTop: `4px solid ${cat.color || '#10b981'}` }}
                                            >
                                                {/* Encabezado de la Categoría */}
                                                <div className="bolsa-card-header">
                                                    <div className="bolsa-card-title-group">
                                                        <span
                                                            className="bolsa-color-dot"
                                                            style={{ backgroundColor: cat.color || '#10b981' }}
                                                        />
                                                        <div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                                <h4 className="bolsa-card-title">{cat.nombre}</h4>
                                                                {cat.esBolsaProyecto && (
                                                                    <span className="bolsa-badge-proy" title="Bolsa vinculada a Cuota Extraordinaria">
                                                                        🚀 Proyecto {cat.montoCuotaExtra ? `($${Number(cat.montoCuotaExtra).toLocaleString('es-MX', { minimumFractionDigits: 2 })} / viv)` : ''}
                                                                    </span>
                                                                )}
                                                                {cat.congelacion?.habilitada && (
                                                                    <span className="bolsa-badge-cong" title="Congelación automática de remanentes">
                                                                        ❄️ Congelada ({cat.congelacion.periodicidad || 'mensual'})
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {cat.descripcion && (
                                                                <p className="bolsa-card-desc">{cat.descripcion}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="bolsa-card-actions">
                                                        <button
                                                            type="button"
                                                            className="bolsa-btn-icon"
                                                            onClick={() => handleOpenEditarCategoria(cat)}
                                                            title="Editar categoría y fuentes de ingreso"
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="bolsa-btn-icon danger"
                                                            onClick={() => setCategoriaParaEliminar(cat)}
                                                            title="Eliminar categoría"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Fondeo de Ingresos Vinculados */}
                                                <div className="bolsa-fondeo-section">
                                                    <span className="bolsa-fondeo-label">Fondeado por:</span>
                                                    <div className="bolsa-fondeo-tags">
                                                        {(cat.conceptosIngresoAsociados && cat.conceptosIngresoAsociados.length > 0) ? (
                                                            cat.conceptosIngresoAsociados.map((ing) => {
                                                                const otrasComparten = obtenerCategoriasQueCompartenConcepto(ing, categoriasEgreso, cat.id);
                                                                const esCompartido = otrasComparten.length > 0;
                                                                return (
                                                                    <span
                                                                        key={ing}
                                                                        className={`bolsa-ingreso-tag ${esCompartido ? 'compartido' : ''}`}
                                                                        title={esCompartido ? `Fondo compartido con: ${otrasComparten.join(', ')}` : 'Fondo exclusivo de esta categoría'}
                                                                    >
                                                                        {esCompartido && <FaExchangeAlt className="bolsa-tag-icon-share" />}
                                                                        <span className="bolsa-tag-text">{ing}</span>
                                                                        {esCompartido && (
                                                                            <span className="bolsa-tag-share-badge">
                                                                                (Compartido con: {otrasComparten.join(', ')})
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                );
                                                            })
                                                        ) : (
                                                            <span className="bolsa-ingreso-tag muted">General / Sin restricción</span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Lista de Conceptos Específicos */}
                                                <div className="bolsa-conceptos-container">
                                                    <div className="bolsa-conceptos-header">
                                                        <span className="bolsa-conceptos-count">
                                                            {conceptosList.length} conceptos específicos
                                                        </span>
                                                        <span className="bolsa-conceptos-tip">
                                                            (Clic en Fijo/Variable para alternar)
                                                        </span>
                                                    </div>

                                                    {conceptosList.length === 0 ? (
                                                        <div className="bolsa-no-conceptos">
                                                            Sin conceptos aún en esta categoría.
                                                        </div>
                                                    ) : (
                                                        <div className="bolsa-conceptos-chips-wrapper">
                                                            {conceptosList.map((c) => {
                                                                const cNombre = typeof c === 'string' ? c : c.nombre;
                                                                const cId = typeof c === 'string' ? c : c.id;
                                                                const cTipo = (typeof c === 'object' && c.tipoGasto) ? c.tipoGasto : 'Variable';

                                                                return (
                                                                    <div key={cId} className="bolsa-concepto-item">
                                                                        <span className="bolsa-concepto-text">{cNombre}</span>
                                                                        <button
                                                                            type="button"
                                                                            className={`bolsa-tipo-pill ${cTipo === 'Fijo' ? 'fijo' : 'variable'}`}
                                                                            onClick={() => handleToggleTipoGastoConcepto(cat.id, cId)}
                                                                            title={`Cambiar a ${cTipo === 'Fijo' ? 'Variable' : 'Fijo'}`}
                                                                        >
                                                                            {cTipo}
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="bolsa-delete-concepto-btn"
                                                                            onClick={() => handleDeleteConceptoEspecifico(cat.id, cId)}
                                                                            title="Eliminar concepto de esta categoría"
                                                                        >
                                                                            ✕
                                                                        </button>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Mini Formulario para agregar concepto específico */}
                                                <div className="bolsa-add-concepto-box">
                                                    <input
                                                        type="text"
                                                        id={`input-concepto-${cat.id}`}
                                                        className="bolsa-add-input"
                                                        placeholder="Ej: Alberca, Vigilancia, CFE..."
                                                        value={nuevoTexto}
                                                        onChange={(e) => setNuevoConceptoTextoPorCat(prev => ({
                                                            ...prev,
                                                            [cat.id]: e.target.value
                                                        }))}
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                handleAddConceptoEspecifico(cat.id);
                                                            }
                                                        }}
                                                        disabled={saving}
                                                    />
                                                    <select
                                                        className="bolsa-select-tipo"
                                                        value={nuevoTipo}
                                                        onChange={(e) => setNuevoConceptoTipoPorCat(prev => ({
                                                            ...prev,
                                                            [cat.id]: e.target.value
                                                        }))}
                                                        disabled={saving}
                                                        title="Tipo de gasto: Fijo (recurrente) o Variable (bajo demanda)"
                                                    >
                                                        <option value="Variable">Variable</option>
                                                        <option value="Fijo">Fijo</option>
                                                    </select>
                                                    <button
                                                        type="button"
                                                        className="bolsa-add-btn"
                                                        onClick={() => handleAddConceptoEspecifico(cat.id)}
                                                        disabled={saving}
                                                        title="Agregar concepto específico a esta categoría"
                                                    >
                                                        <FaPlus />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB 4: Vista Previa del Recibo */}
                    {tab === 4 && (
                        <div className="receipt-tab-inner">
                            <div className="section-banner-premium gold-gradient">
                                <div className="banner-index">5</div>
                                <div className="banner-info">
                                    <h3>Vista previa de recibos</h3>
                                    <p>Así se verán los recibos de pago y egreso</p>
                                </div>
                            </div>

                            <div className="preview-container-premium">
                                <div className="preview-card-wrapper">
                                    <h4 className="preview-label">Recibo de Ingreso (Vista Previa)</h4>
                                    <div className="receipt-preview-premium">
                                        <div className="preview-header-main">
                                            <div className="preview-logo-space">
                                                {receiptConfig.logoUrl ? (
                                                    <img src={toPublicUrl(receiptConfig.logoUrl)} alt="Logo" className="preview-logo-img" style={{ width: `${receiptConfig.logoWidth}px`, height: `${receiptConfig.logoHeight}px` }} />
                                                ) : (
                                                    <div className="preview-logo-placeholder">Logo</div>
                                                )}
                                                <div className="preview-condo-info">
                                                    <h4>{receiptConfig.condominioNombre || 'Residencial / Condominio'}</h4>
                                                    <p>{receiptConfig.condominioDireccion || 'Dirección del condominio'}</p>
                                                </div>
                                            </div>
                                            <div className="preview-document-meta">
                                                <h3 className="document-title">RECIBO DE PAGO</h3>
                                                <div className="meta-details">
                                                    <p>Folio: <strong>{folioPreview}</strong></p>
                                                    <p>Fecha: {formatoFecha(fechaPagoPreview)}</p>
                                                    <p className="emision-info">Emisión: {formatoFecha(fechaEmisionPreview)} {fechaEmisionPreview.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="preview-body-content">
                                            <div className="info-block-premium">
                                                <h5>Datos de Facturación</h5>
                                                <p><strong>Razón Social:</strong> {receiptConfig.razonSocial || 'Condominio / Razón Social'}</p>
                                                <p><strong>RFC:</strong> {receiptConfig.rfc || 'RFC123456ABC'}</p>
                                                {receiptConfig.datosFacturacion && <p className="fiscal-data-small">{receiptConfig.datosFacturacion}</p>}
                                            </div>

                                            <div className="preview-info-stack-premium">
                                                <div className="info-block-premium">
                                                    <h5>Administrador</h5>
                                                    <p><strong>Nombre:</strong> {receiptConfig.administradorNombre || 'Administrador'}</p>
                                                    <p className="address-small">{receiptConfig.adminDireccion || 'Dirección del administrador'}</p>
                                                </div>
                                                <div className="info-block-premium">
                                                    <h5>Residente</h5>
                                                    <p><strong>Casa:</strong> 009 | <strong>Nombre:</strong> Ejemplo Vista P</p>
                                                    <p className="contact-small">ejemplo@correo.com | 4421234567</p>
                                                </div>
                                            </div>

                                            <div className="table-block-premium">
                                                <div className="modern-preview-table">
                                                    <div className="table-head">
                                                        <span>Concepto</span>
                                                        <span className="text-right">Importe</span>
                                                    </div>
                                                    {previewConceptos.map((c, idx) => (
                                                        <div className="table-row-item" key={c + idx}>
                                                            <span>{c}</span>
                                                            <span className="text-right">{new Intl.NumberFormat('es-MX', { style: 'currency', currency: receiptConfig.moneda || 'MXN' }).format(previewImportes[idx])}</span>
                                                        </div>
                                                    ))}
                                                    <div className="table-foot-total">
                                                        <span>TOTAL</span>
                                                        <span className="total-amount-highlight">{new Intl.NumberFormat('es-MX', { style: 'currency', currency: receiptConfig.moneda || 'MXN' }).format(totalPreview)}</span>
                                                    </div>
                                                </div>
                                                <p className="amount-letters">SON: {totalEnLetras}</p>
                                            </div>
                                        </div>

                                        <div className="preview-footer-signature-centered">
                                            <div className="signature-area">
                                                {receiptConfig.selloUrl ? (
                                                    <img src={toPublicUrl(receiptConfig.selloUrl)} alt="Sello" className="preview-seal-img" style={{ width: `${receiptConfig.selloWidth}px`, height: `${receiptConfig.selloHeight}px` }} />
                                                ) : (
                                                    <div className="preview-seal-placeholder">Sello / Firma</div>
                                                )}
                                                <div className="signature-line-box">
                                                    <div className="signature-line" />
                                                    <small>Firma y Sello Digital</small>
                                                    <p>{receiptConfig.administradorNombre || 'Administrador'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="preview-card-wrapper">
                                    <h4 className="preview-label">Recibo de Egreso (Vista Previa)</h4>
                                    <div className="receipt-preview-premium">
                                        <div className="preview-header-main">
                                            <div className="preview-logo-space">
                                                {receiptConfig.logoUrl ? (
                                                    <img src={toPublicUrl(receiptConfig.logoUrl)} alt="Logo" className="preview-logo-img" style={{ width: `${receiptConfig.logoWidth}px`, height: `${receiptConfig.logoHeight}px` }} />
                                                ) : (
                                                    <div className="preview-logo-placeholder">Logo</div>
                                                )}
                                                <div className="preview-condo-info">
                                                    <h4>{receiptConfig.condominioNombre || 'Residencial / Condominio'}</h4>
                                                    <p>{receiptConfig.condominioDireccion || 'Dirección del condominio'}</p>
                                                </div>
                                            </div>
                                            <div className="preview-document-meta">
                                                <h3 className="document-title">RECIBO DE EGRESO</h3>
                                                <div className="meta-details">
                                                    <p>Folio: <strong>{folioPreviewEgreso}</strong></p>
                                                    <p>Fecha: {formatoFecha(fechaPagoPreview)}</p>
                                                    <p className="emision-info">Emisión: {formatoFecha(fechaEmisionPreview)} {fechaEmisionPreview.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="preview-body-content">
                                            <div className="info-block-premium">
                                                <h5>Datos de Facturación</h5>
                                                <p><strong>Razón Social:</strong> {receiptConfig.razonSocial || 'Condominio / Razón Social'}</p>
                                                <p><strong>RFC:</strong> {receiptConfig.rfc || 'RFC123456ABC'}</p>
                                                {receiptConfig.datosFacturacion && <p className="fiscal-data-small">{receiptConfig.datosFacturacion}</p>}
                                            </div>

                                            <div className="preview-info-stack-premium">
                                                <div className="info-block-premium">
                                                    <h5>Administrador</h5>
                                                    <p><strong>Nombre:</strong> {receiptConfig.administradorNombre || 'Administrador'}</p>
                                                    <p className="address-small">{receiptConfig.adminDireccion || 'Dirección del administrador'}</p>
                                                </div>
                                                <div className="info-block-premium">
                                                    <h5>Proveedor</h5>
                                                    <p><strong>Nombre:</strong> Proveedor demo</p>
                                                    <p className="contact-small">Ref / Factura: FAC-00999 | Método: {previewMetodoPagoEgreso}</p>
                                                </div>
                                            </div>

                                            <div className="table-block-premium">
                                                <div className="modern-preview-table">
                                                    <div className="table-head">
                                                        <span>Concepto</span>
                                                        <span className="text-right">Importe</span>
                                                    </div>
                                                    {previewConceptosEgreso.map((c, idx) => (
                                                        <div className="table-row-item" key={c + idx}>
                                                            <span>{c}</span>
                                                            <span className="text-right">{new Intl.NumberFormat('es-MX', { style: 'currency', currency: receiptConfig.moneda || 'MXN' }).format(previewImportesEgreso[idx])}</span>
                                                        </div>
                                                    ))}
                                                    <div className="table-foot-total">
                                                        <span>TOTAL</span>
                                                        <span className="total-amount-highlight">{new Intl.NumberFormat('es-MX', { style: 'currency', currency: receiptConfig.moneda || 'MXN' }).format(totalPreviewEgreso)}</span>
                                                    </div>
                                                </div>
                                                <p className="amount-letters">SON: {totalEnLetrasEgreso}</p>
                                            </div>
                                        </div>

                                        <div className="preview-footer-signature-centered">
                                            <div className="signature-area">
                                                {receiptConfig.selloUrl ? (
                                                    <img src={toPublicUrl(receiptConfig.selloUrl)} alt="Sello" className="preview-seal-img" style={{ width: `${receiptConfig.selloWidth}px`, height: `${receiptConfig.selloHeight}px` }} />
                                                ) : (
                                                    <div className="preview-seal-placeholder">Sello / Firma</div>
                                                )}
                                                <div className="signature-line-box">
                                                    <div className="signature-line" />
                                                    <small>Firma y Sello Digital</small>
                                                    <p>{receiptConfig.administradorNombre || 'Administrador'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                </div>

                {
                    tab !== 4 && (
                        <div className="receipts-footer-actions">
                            <div className="indicator-wrapper">
                                {saving ? (
                                    <span className="saving-pill pulse">Guardando cambios...</span>
                                ) : (
                                    lastSavedAt && <span className="saved-pill">✓ Último guardado: {new Date(lastSavedAt).toLocaleTimeString()}</span>
                                )}
                            </div>
                            <button type="submit" className="btn-save-premium" disabled={saving || loading}>
                                {saving ? 'Guardando...' : 'Guardar Configuración'}
                            </button>
                        </div>
                    )
                }
                {loading && <p className="config-status">Cargando configuración de recibos…</p>}
            </form>

            {showUnsavedPrompt && (
                <div className="unsaved-overlay">
                    <div className="unsaved-modal">
                        <div className="unsaved-icon" aria-hidden="true">!</div>
                        <h4>Cambios sin guardar</h4>
                        <p>Si cambias de pestaña sin guardar, podrías perder tus ajustes.</p>
                        <div className="unsaved-actions">
                            <button
                                type="button"
                                className="unsaved-secondary"
                                onClick={() => {
                                    setPendingTab(null);
                                    setShowUnsavedPrompt(false);
                                }}
                            >
                                Volver
                            </button>
                            <button
                                type="button"
                                className="unsaved-primary"
                                onClick={() => {
                                    if (pendingTab !== null) setTab(pendingTab);
                                    setPendingTab(null);
                                    setShowUnsavedPrompt(false);
                                }}
                            >
                                Continuar sin guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showPriceModal && (
                <div className="unsaved-overlay">
                    <div className="unsaved-modal price-modal">
                        <div className="modal-header-custom">
                            <h4>Configurar Concepto</h4>
                        </div>
                        <p className="concept-name-highlight">"{pendingConcept}"</p>
                        <p>¿Este concepto tiene un cargo fijo?</p>

                        <div className="form-group-premium">
                            <label>Monto fijo (opcional)</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="modern-input"
                                placeholder="Ej: 500.00"
                                value={conceptPrice}
                                onChange={(e) => setConceptPrice(e.target.value)}
                                disabled={saving}
                            />
                            <small className="config-hint-premium">
                                Si dejas este campo vacío, el concepto se agregará sin precio fijo.
                            </small>
                        </div>

                        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '0.82rem', color: '#92400e', textAlign: 'left', lineHeight: '1.4' }}>
                            <strong>💼 Clasificación Contable:</strong> Este concepto se agregará a la <strong>Categoría B (Ingreso de Tesorería)</strong>. Los pagos asociados representarán ingresos para el fraccionamiento y no alterarán los saldos adeudados de los residentes.
                        </div>

                        <div className="unsaved-actions">
                            <button
                                type="button"
                                className="unsaved-secondary"
                                onClick={() => {
                                    setPendingConcept('');
                                    setConceptPrice('');
                                    setShowPriceModal(false);
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="unsaved-primary"
                                onClick={() => handleSaveConceptWithPrice(!!conceptPrice && parseFloat(conceptPrice) > 0)}
                            >
                                {conceptPrice && parseFloat(conceptPrice) > 0 ? 'Agregar con precio fijo' : 'Agregar sin precio fijo'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {
                confirmDeleteOpen && (
                    <ConfirmDialog
                        open={confirmDeleteOpen}
                        title="Eliminar concepto"
                        message={`¿Eliminar el concepto "${confirmDeleteLabel}"?`}
                        confirmLabel="Aceptar"
                        cancelLabel="Cancelar"
                        tone="danger"
                        theme="light"
                        onCancel={() => setConfirmDeleteOpen(false)}
                        onConfirm={async () => {
                            const updatedConceptos = reorderOtrosLast(conceptos.filter((item) => normalizeLabel(item) !== normalizeLabel(confirmDeleteLabel)));
                            setConceptos(updatedConceptos);
                            setConfirmDeleteOpen(false);

                            // Guardar automáticamente
                            try {
                                setSaving(true);
                                const refFracc = doc(db, 'fraccionamientos', fraccionamientoId);
                                const conceptosObject = updatedConceptos.reduce((acc, label, index) => {
                                    acc[`concepto_${index + 1}`] = label;
                                    return acc;
                                }, {});
                                const conceptosEgresoObject = conceptosEgreso.reduce((acc, label, index) => {
                                    acc[`concepto_egreso_${index + 1}`] = label;
                                    return acc;
                                }, {});
                                await setDoc(refFracc, {
                                    configuracionRecibos: {
                                        ...receiptConfig,
                                        conceptos: conceptosObject,
                                        conceptosList: reorderOtrosLast(updatedConceptos),
                                        conceptosEgreso: conceptosEgresoObject,
                                        conceptosEgresoList: reorderOtrosLast(conceptosEgreso),
                                    },
                                }, { merge: true });
                                setLastSavedAt(new Date());
                                setDirty(false);
                                showNotification(`Concepto "${confirmDeleteLabel}" eliminado y guardado.`, 'success');
                            } catch (error) {
                                console.error('Error guardando después de eliminar:', error);
                                showNotification('El concepto se eliminó pero hubo un error al guardar. Intenta guardar manualmente.', 'error');
                            } finally {
                                setSaving(false);
                            }
                        }}
                    />
                )
            }

            {
                confirmDeleteEgresoOpen && (
                    <ConfirmDialog
                        open={confirmDeleteEgresoOpen}
                        title="Eliminar concepto de egreso"
                        message={`¿Eliminar el concepto de egreso "${confirmDeleteEgresoLabel}"?`}
                        confirmLabel="Aceptar"
                        cancelLabel="Cancelar"
                        tone="danger"
                        theme="light"
                        onCancel={() => setConfirmDeleteEgresoOpen(false)}
                        onConfirm={async () => {
                            const updatedConceptosEgreso = reorderOtrosLast(conceptosEgreso.filter((item) => normalizeLabel(item) !== normalizeLabel(confirmDeleteEgresoLabel)));
                            setConceptosEgreso(updatedConceptosEgreso);
                            setConfirmDeleteEgresoOpen(false);

                            // Guardar automáticamente
                            try {
                                setSaving(true);
                                const refFracc = doc(db, 'fraccionamientos', fraccionamientoId);
                                const conceptosObject = conceptos.reduce((acc, label, index) => {
                                    acc[`concepto_${index + 1}`] = label;
                                    return acc;
                                }, {});
                                const conceptosEgresoObject = updatedConceptosEgreso.reduce((acc, label, index) => {
                                    acc[`concepto_egreso_${index + 1}`] = label;
                                    return acc;
                                }, {});
                                await setDoc(refFracc, {
                                    configuracionRecibos: {
                                        ...receiptConfig,
                                        conceptos: conceptosObject,
                                        conceptosList: reorderOtrosLast(conceptos),
                                        conceptosEgreso: conceptosEgresoObject,
                                        conceptosEgresoList: reorderOtrosLast(updatedConceptosEgreso),
                                    },
                                }, { merge: true });
                                setLastSavedAt(new Date());
                                setDirty(false);
                                showNotification(`Concepto de egreso "${confirmDeleteEgresoLabel}" eliminado y guardado.`, 'success');
                            } catch (error) {
                                console.error('Error guardando después de eliminar egreso:', error);
                                showNotification('El concepto se eliminó pero hubo un error al guardar. Intenta guardar manualmente.', 'error');
                            } finally {
                                setSaving(false);
                            }
                        }}
                    />
                )
            }

            {/* MODAL PARA CREAR / EDITAR CATEGORÍA DE EGRESO */}
            {showModalCategoria && (
                <div className="bolsa-modal-overlay" onClick={() => setShowModalCategoria(false)}>
                    <div className="bolsa-modal-card" onClick={(e) => e.stopPropagation()}>
                        <div className="bolsa-modal-header">
                            <h3>{categoriaEditando ? 'Editar Categoría de Egreso' : 'Nueva Categoría de Egreso'}</h3>
                            <button
                                type="button"
                                className="bolsa-modal-close"
                                onClick={() => setShowModalCategoria(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className="bolsa-modal-body">
                            <div className="bolsa-form-group">
                                <label className="bolsa-form-label">Nombre de la Categoría *</label>
                                <input
                                    type="text"
                                    className="modern-input"
                                    placeholder="Ej: Servicio, Insumo, Proyecto Techumbre Asadores..."
                                    value={categoriaForm.nombre}
                                    onChange={(e) => setCategoriaForm(prev => ({ ...prev, nombre: e.target.value }))}
                                />
                            </div>

                            <div className="bolsa-form-group">
                                <label className="bolsa-form-label">Descripción (Opcional)</label>
                                <input
                                    type="text"
                                    className="modern-input"
                                    placeholder="Ej: Ahorro para techumbre y asadores de áreas comunes"
                                    value={categoriaForm.descripcion}
                                    onChange={(e) => setCategoriaForm(prev => ({ ...prev, descripcion: e.target.value }))}
                                />
                            </div>

                            <div className="bolsa-form-group">
                                <label className="bolsa-form-label">Color Distintivo</label>
                                <div className="bolsa-color-palette">
                                    {['#10b981', '#3b82f6', '#6366f1', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899'].map((col) => (
                                        <button
                                            key={col}
                                            type="button"
                                            className={`bolsa-color-swatch ${categoriaForm.color === col ? 'active' : ''}`}
                                            style={{ backgroundColor: col }}
                                            onClick={() => setCategoriaForm(prev => ({ ...prev, color: col }))}
                                        >
                                            {categoriaForm.color === col && <FaCheck color="#fff" size={12} />}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* SELECTOR DE PROPÓSITO: OPERATIVA REGULAR VS PROYECTO ESPECIAL */}
                            <div className="bolsa-form-group">
                                <label className="bolsa-form-label">Tipo y Propósito de la Bolsa</label>
                                <div className="bolsa-tipo-selector">
                                    <button
                                        type="button"
                                        className={`bolsa-tipo-btn ${!categoriaForm.esBolsaProyecto ? 'active' : ''}`}
                                        onClick={() => setCategoriaForm(prev => ({ ...prev, esBolsaProyecto: false }))}
                                    >
                                        <FaFolder style={{ marginRight: '6px' }} />
                                        <span>Bolsa Operativa Regular</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`bolsa-tipo-btn ${categoriaForm.esBolsaProyecto ? 'active' : ''}`}
                                        onClick={() => setCategoriaForm(prev => ({ ...prev, esBolsaProyecto: true }))}
                                    >
                                        <FaProjectDiagram style={{ marginRight: '6px' }} />
                                        <span>Bolsa de Proyecto Especial</span>
                                    </button>
                                </div>
                            </div>

                            {/* SI ES BOLSA DE PROYECTO: CONFIGURACIÓN DE CUOTA EXTRAORDINARIA VINCULADA */}
                            {categoriaForm.esBolsaProyecto ? (
                                <div className="bolsa-proyecto-card">
                                    <div className="bolsa-proyecto-card-header">
                                        <FaCoins style={{ color: '#8b5cf6', fontSize: '1.2rem' }} />
                                        <div>
                                            <h5 style={{ margin: 0, color: '#4c1d95', fontSize: '0.95rem' }}>
                                                Vinculación Automática a Cuota Extraordinaria
                                            </h5>
                                            <p style={{ margin: '3px 0 0 0', fontSize: '0.78rem', color: '#6d28d9' }}>
                                                Esta bolsa generará un cargo mensual/periódico a todas las propiedades y alimentará el medidor de recaudación y avance en tiempo real.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="bolsa-proyecto-grid">
                                        <div className="bolsa-field">
                                            <label>Monto por Vivienda ($ MXN) *</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0.01"
                                                placeholder="Ej: 500.00"
                                                value={categoriaForm.montoCuotaExtra}
                                                onChange={(e) => setCategoriaForm(prev => ({ ...prev, montoCuotaExtra: e.target.value }))}
                                                className="modern-input"
                                                required
                                            />
                                        </div>

                                        <div className="bolsa-field">
                                            <label>Periodicidad de Cobro *</label>
                                            <select
                                                className="modern-input"
                                                value={categoriaForm.frecuenciaCuotaExtra || 'monthly'}
                                                onChange={(e) => setCategoriaForm(prev => ({ ...prev, frecuenciaCuotaExtra: e.target.value }))}
                                            >
                                                <option value="monthly">Mes a mes (Mensual)</option>
                                                <option value="bimonthly">Bimestral</option>
                                                <option value="quarterly">Trimestral</option>
                                                <option value="semiannual">Semestral</option>
                                                <option value="yearly">Cobro único (Anual)</option>
                                            </select>
                                        </div>

                                        <div className="bolsa-field">
                                            <label>Mes de Inicio *</label>
                                            <select
                                                className="modern-input"
                                                value={categoriaForm.mesInicioCuotaExtra !== undefined ? categoriaForm.mesInicioCuotaExtra : new Date().getMonth() + 1}
                                                onChange={(e) => setCategoriaForm(prev => ({ ...prev, mesInicioCuotaExtra: parseInt(e.target.value, 10) }))}
                                            >
                                                <option value={1}>Enero</option>
                                                <option value={2}>Febrero</option>
                                                <option value={3}>Marzo</option>
                                                <option value={4}>Abril</option>
                                                <option value={5}>Mayo</option>
                                                <option value={6}>Junio</option>
                                                <option value={7}>Julio</option>
                                                <option value={8}>Agosto</option>
                                                <option value={9}>Septiembre</option>
                                                <option value={10}>Octubre</option>
                                                <option value={11}>Noviembre</option>
                                                <option value={12}>Diciembre</option>
                                            </select>
                                        </div>

                                        <div className="bolsa-field">
                                            <label>Duración del Proyecto (Meses) *</label>
                                            <input
                                                type="number"
                                                min="1"
                                                max="60"
                                                placeholder="Ej: 6"
                                                value={categoriaForm.duracionMesesCuotaExtra}
                                                onChange={(e) => setCategoriaForm(prev => ({ ...prev, duracionMesesCuotaExtra: e.target.value }))}
                                                className="modern-input"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* SI ES BOLSA REGULAR: SELECCIÓN DE FONDOS DE INGRESO */
                                <div className="bolsa-form-group">
                                    <label className="bolsa-form-label">
                                        Bolsas de Ingreso que fondean esta categoría
                                        <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 'normal', marginLeft: '6px' }}>
                                            (Los egresos de esta categoría se restarán de estos ingresos)
                                        </span>
                                    </label>
                                    <div className="bolsa-modal-compartido-tip">
                                        <FaInfoCircle size={14} style={{ marginRight: '6px', flexShrink: 0, marginTop: '2px' }} />
                                        <span>
                                            <strong>Bolsa Madre Compartida:</strong> Puedes vincular conceptos que ya pertenezcan a otra categoría. Formarán un fondo común compartido del cual cada categoría descontará sus egresos correspondientes sin duplicar dinero.
                                        </span>
                                    </div>
                                    <div className="bolsa-ingresos-multiselect">
                                        {conceptos.map((ing) => {
                                            const isChecked = (categoriaForm.conceptosIngresoAsociados || []).includes(ing);
                                            const otrasComparten = obtenerCategoriasQueCompartenConcepto(ing, categoriasEgreso, categoriaEditando?.id || categoriaForm.id);
                                            const esCompartido = otrasComparten.length > 0;
                                            const estaSinAsignar = !isChecked && otrasComparten.length === 0;
                                            return (
                                                <button
                                                    key={ing}
                                                    type="button"
                                                    className={`bolsa-ingreso-toggle-btn ${isChecked ? 'selected' : ''} ${esCompartido ? 'tiene-compartido' : ''} ${estaSinAsignar ? 'sin-asignar' : ''}`}
                                                    onClick={() => {
                                                        setCategoriaForm(prev => {
                                                            const current = prev.conceptosIngresoAsociados || [];
                                                            const next = current.includes(ing)
                                                                ? current.filter(x => x !== ing)
                                                                : [...current, ing];
                                                            return { ...prev, conceptosIngresoAsociados: next };
                                                        });
                                                    }}
                                                    title={
                                                        esCompartido
                                                            ? `Este concepto ya fondea a: ${otrasComparten.join(', ')}. Al seleccionarlo compartirán esta bolsa madre.`
                                                            : estaSinAsignar
                                                            ? 'Actualmente no fondea ninguna categoría. Selecciónalo para asignarlo a esta bolsa.'
                                                            : 'Concepto exclusivo para esta categoría'
                                                    }
                                                >
                                                    <div className="bolsa-btn-content-stacked">
                                                        <div className="bolsa-btn-title-row">
                                                            {isChecked && <FaCheck size={10} style={{ marginRight: '5px' }} />}
                                                            <span>{ing}</span>
                                                        </div>
                                                        {esCompartido && (
                                                            <div className="bolsa-btn-shared-sub">
                                                                <FaExchangeAlt size={9} style={{ marginRight: '3px' }} />
                                                                <span>Compartido con: {otrasComparten.join(', ')}</span>
                                                            </div>
                                                        )}
                                                        {estaSinAsignar && (
                                                            <div className="bolsa-btn-unassigned-sub">
                                                                <FaExclamationTriangle size={9} style={{ marginRight: '3px' }} />
                                                                <span>Sin bolsa de destino</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* SECCIÓN DE CONGELACIÓN DE REMANENTES A FIN DE MES / PERIODO */}
                            <div className="bolsa-congelacion-card">
                                <div className="bolsa-congelacion-header">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <FaSnowflake style={{ color: '#0284c7', fontSize: '1.2rem' }} />
                                        <div>
                                            <h5 style={{ margin: 0, color: '#0369a1', fontSize: '0.95rem' }}>
                                                Congelación Periódica de Remanentes
                                            </h5>
                                            <span style={{ fontSize: '0.78rem', color: '#0284c7' }}>
                                                Aparta el saldo no ejercido a fin de periodo blindándolo para reserva o compensación de imprevistos.
                                            </span>
                                        </div>
                                    </div>
                                    <label className="bolsa-switch">
                                        <input
                                            type="checkbox"
                                            checked={Boolean(categoriaForm.congelacion?.habilitada)}
                                            onChange={(e) => setCategoriaForm(prev => ({
                                                ...prev,
                                                congelacion: {
                                                    ...prev.congelacion,
                                                    habilitada: e.target.checked
                                                }
                                            }))}
                                        />
                                        <span className="bolsa-slider" />
                                    </label>
                                </div>

                                {categoriaForm.congelacion?.habilitada && (
                                    <div className="bolsa-congelacion-body">
                                        <div className="bolsa-proyecto-grid">
                                            <div className="bolsa-field">
                                                <label>Periodicidad de Bloqueo *</label>
                                                <select
                                                    className="modern-input"
                                                    value={categoriaForm.congelacion?.periodicidad || 'mensual'}
                                                    onChange={(e) => setCategoriaForm(prev => ({
                                                        ...prev,
                                                        congelacion: {
                                                            ...prev.congelacion,
                                                            periodicidad: e.target.value
                                                        }
                                                    }))}
                                                >
                                                    <option value="mensual">Mensual (Al fin de cada mes)</option>
                                                    <option value="trimestral">Trimestral (Cada 3 meses)</option>
                                                    <option value="semestral">Semestral (Cada 6 meses)</option>
                                                    <option value="anual">Anual (Al cierre de año)</option>
                                                </select>
                                            </div>

                                            <div className="bolsa-field">
                                                <label>Saldo Congelado Inicial ($ MXN)</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    placeholder="0.00"
                                                    value={categoriaForm.congelacion?.saldoCongeladoAcumulado || ''}
                                                    onChange={(e) => setCategoriaForm(prev => ({
                                                        ...prev,
                                                        congelacion: {
                                                            ...prev.congelacion,
                                                            saldoCongeladoAcumulado: parseFloat(e.target.value || 0)
                                                        }
                                                    }))}
                                                    className="modern-input"
                                                />
                                            </div>
                                        </div>
                                        <p style={{ margin: '8px 0 0 0', fontSize: '0.75rem', color: '#0369a1' }}>
                                            💡 <em>Los fondos congelados de esta bolsa podrán ser seleccionados para compensar déficits en otras bolsas durante el registro de nuevos egresos.</em>
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="bolsa-modal-footer">
                            <button
                                type="button"
                                className="btn-zen-secondary"
                                onClick={() => setShowModalCategoria(false)}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn-zen-primary"
                                onClick={handleGuardarCategoriaModal}
                                disabled={saving}
                            >
                                {saving ? 'Guardando...' : (categoriaEditando ? 'Guardar Cambios' : 'Crear Categoría')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* CONFIRMAR ELIMINAR CATEGORÍA */}
            {categoriaParaEliminar && (
                <ConfirmDialog
                    open={Boolean(categoriaParaEliminar)}
                    title="Eliminar Categoría de Egreso"
                    message={`¿Estás seguro de eliminar la categoría "${categoriaParaEliminar.nombre}" y todos sus conceptos específicos asociados? Esta acción no se puede deshacer.`}
                    confirmLabel="Eliminar Categoría"
                    cancelLabel="Cancelar"
                    tone="danger"
                    theme="light"
                    onCancel={() => setCategoriaParaEliminar(null)}
                    onConfirm={handleConfirmarEliminarCategoria}
                />
            )}

            {/* CONFIRMAR CARGAR PLANTILLA OFICIAL AGAVE V */}
            {confirmPlantillaOpen && (
                <ConfirmDialog
                    open={confirmPlantillaOpen}
                    title="Cargar Plantilla Oficial Agave V"
                    message="¿Deseas cargar la plantilla de 6 categorías y conceptos del Diario de Banco de Agave Azul V? Esto reemplazará la estructura actual en pantalla (deberás presionar 'Guardar Configuración' para confirmar los cambios en la base de datos)."
                    confirmLabel="Cargar Plantilla"
                    cancelLabel="Cancelar"
                    tone="primary"
                    theme="light"
                    onCancel={() => setConfirmPlantillaOpen(false)}
                    onConfirm={handleCargarPlantillaOficialAgave}
                />
            )}
        </div>
    );
};

export default ConfiguracionRecibos;

